import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { supabase, normalizeError } from '@/lib/supabaseClient'
import { useAuth } from '@/features/auth/AuthContext'
import { generateDraftUnits } from '@/features/units/api/unitsApi'
import AddressAutocomplete from '@/components/AddressAutocomplete'

export default function CreateProjectPage() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [error, setError] = useState(null)
  const [step, setStep] = useState(1)

  const [formData, setFormData] = useState({
    title: '',
    address: '',
    latitude: null,
    longitude: null,
    total_units_planned: '',
    project_type: 'townhouse',
    floor_count: '',
  })

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert({
          developer_id: profile.id,
          name: data.title,
          address: data.address,
          location: data.address,
          latitude: data.latitude,
          longitude: data.longitude,
          description: '',
          status: 'draft',
          project_type: data.project_type,
          floor_count: data.project_type === 'apartment' && data.floor_count ? parseInt(data.floor_count) : null,
        })
        .select()
        .single()

      if (projectError) throw projectError

      if (data.total_units_planned > 0) {
        const { error: unitsError } = await generateDraftUnits(
          project.id,
          parseInt(data.total_units_planned)
        )
        if (unitsError) throw unitsError
      }

      return project
    },
    onSuccess: (project) => {
      navigate(`/developer/projects/${project.id}/edit`)
    },
    onError: (error) => {
      const normalized = normalizeError(error)
      setError(normalized.message)
    },
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address: addressData.address,
      latitude: addressData.latitude,
      longitude: addressData.longitude,
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError(null)

    if (!formData.title || !formData.address || !formData.total_units_planned) {
      setError('Please fill in all required fields')
      return
    }

    if (!formData.latitude || !formData.longitude) {
      setError('Please select a valid address from the suggestions')
      return
    }

    if (parseInt(formData.total_units_planned) < 1) {
      setError('Total units must be at least 1')
      return
    }

    if (formData.project_type === 'apartment' && (!formData.floor_count || parseInt(formData.floor_count) < 1)) {
      setError('Please specify the number of floors for the apartment building')
      return
    }

    createMutation.mutate(formData)
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-secondary-900 mb-2">Create New Project</h1>
        <p className="text-secondary-600">Get started with just three simple details</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="card p-8 space-y-6">
        <div className="space-y-6">
          <div>
            <label htmlFor="title" className="label">
              Project Name *
            </label>
            <input
              id="title"
              name="title"
              type="text"
              value={formData.title}
              onChange={handleChange}
              className="input"
              required
              placeholder="e.g., Harbour View Residences"
            />
            <p className="text-xs text-secondary-500 mt-1">
              Give your project a memorable name
            </p>
          </div>

          <div>
            <label htmlFor="address" className="label">
              Property Address *
            </label>
            <AddressAutocomplete
              value={formData.address}
              onChange={(value) => setFormData(prev => ({ ...prev, address: value }))}
              onSelect={handleAddressSelect}
              placeholder="e.g., 123 Queen Street, Auckland"
              required
            />
            <p className="text-xs text-secondary-500 mt-1">
              Start typing to search for an address. Select from suggestions to automatically get map coordinates.
            </p>
          </div>

          <div>
            <label htmlFor="total_units_planned" className="label">
              Total Number of Units *
            </label>
            <input
              id="total_units_planned"
              name="total_units_planned"
              type="number"
              min="1"
              value={formData.total_units_planned}
              onChange={handleChange}
              className="input"
              required
              placeholder="e.g., 24"
            />
            <p className="text-xs text-secondary-500 mt-1">
              {formData.total_units_planned && parseInt(formData.total_units_planned) > 0
                ? `${formData.total_units_planned} draft units will be automatically created`
                : 'Units will be created in draft status and can be configured later'}
            </p>
          </div>

          <div>
            <label htmlFor="project_type" className="label">
              Project Type *
            </label>
            <select
              id="project_type"
              name="project_type"
              value={formData.project_type}
              onChange={handleChange}
              className="input"
              required
            >
              <option value="townhouse">Townhouse Development</option>
              <option value="apartment">Apartment Building</option>
            </select>
            <p className="text-xs text-secondary-500 mt-1">
              {formData.project_type === 'townhouse'
                ? 'Single development scheme will be required'
                : 'Scheme will be needed for each floor'}
            </p>
          </div>

          {formData.project_type === 'apartment' && (
            <div>
              <label htmlFor="floor_count" className="label">
                Number of Floors *
              </label>
              <input
                id="floor_count"
                name="floor_count"
                type="number"
                min="1"
                value={formData.floor_count}
                onChange={handleChange}
                className="input"
                required={formData.project_type === 'apartment'}
                placeholder="e.g., 5"
              />
              <p className="text-xs text-secondary-500 mt-1">
                Include all floors: lower ground, ground, and upper levels
              </p>
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
          <div className="flex">
            <svg
              className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">What happens next?</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Your project will be created in draft status</li>
                <li>Draft units will be automatically generated</li>
                <li>You can configure unit types, pricing, and details</li>
                <li>Add photos, descriptions, and other information</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-4 pt-6 border-t border-secondary-200">
          <button
            type="button"
            onClick={() => navigate('/dashboard/developer')}
            className="btn-outline flex-1 py-3"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="btn-primary flex-1 py-3"
          >
            {createMutation.isPending ? 'Creating Project...' : 'Create Project'}
          </button>
        </div>
      </form>
    </div>
  )
}
